import { useState } from "react";
import { supabase } from "../../../lib/supabase";

interface Props {
  staffCode: string;
  staffName: string;
  onSuccess?: () => void;
}

const StaffChangePassword = ({ staffCode, staffName, onSuccess }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!currentPwd || !newPwd || !confirmPwd) {
      setError("Vui lòng điền đầy đủ tất cả các trường.");
      return;
    }
    if (newPwd.length < 6) {
      setError("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("Mật khẩu xác nhận không khớp.");
      return;
    }

    setSaving(true);
    try {
      // Verify current password
      const { data: staff, error: fetchErr } = await supabase
        .from("staff")
        .select("password")
        .eq("m_code", staffCode)
        .maybeSingle();

      if (fetchErr || !staff) {
        setError("Không thể xác thực tài khoản. Vui lòng thử lại.");
        setSaving(false);
        return;
      }

      if (staff.password !== currentPwd) {
        setError("Mật khẩu hiện tại không đúng.");
        setSaving(false);
        return;
      }

      // Update password — Super Admin can see this in their dashboard
      const { error: updateErr } = await supabase
        .from("staff")
        .update({ password: newPwd, updated_at: new Date().toISOString() })
        .eq("m_code", staffCode);

      if (updateErr) {
        setError("Lỗi cập nhật mật khẩu. Vui lòng thử lại.");
        setSaving(false);
        return;
      }

      // Update localStorage auth
      const stored = localStorage.getItem("bizwell_auth");
      if (stored) {
        const auth = JSON.parse(stored);
        localStorage.setItem("bizwell_auth", JSON.stringify(auth));
      }

      setSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
        setCurrentPwd("");
        setNewPwd("");
        setConfirmPwd("");
        onSuccess?.();
      }, 2000);
    } catch {
      setError("Lỗi hệ thống. Vui lòng thử lại.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99] transition-colors pr-10";

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-4 py-2 bg-[#004A99]/10 hover:bg-[#004A99]/20 text-[#004A99] text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-lock-password-line" />
        Đổi mật khẩu
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="bg-[#004A99] px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className="ri-lock-password-line text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Đổi Mật Khẩu</h3>
                    <p className="text-white/70 text-xs">{staffName} · {staffCode}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowModal(false); setError(""); setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); }}
                  className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-white" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {success ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-green-500 text-2xl" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">Đổi mật khẩu thành công!</h4>
                  <p className="text-sm text-gray-500">Mật khẩu mới đã được cập nhật và hiển thị trên bảng Super Admin.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                    <i className="ri-information-line mr-1" />
                    Mật khẩu mới sẽ được cập nhật ngay lên bảng quản lý của Super Admin.
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2.5 flex items-center gap-2">
                      <i className="ri-error-warning-line" />
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mật khẩu hiện tại *</label>
                    <div className="relative">
                      <input
                        type={showCurrent ? "text" : "password"}
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        placeholder="Nhập mật khẩu hiện tại"
                        className={inputCls}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent(!showCurrent)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={`${showCurrent ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mật khẩu mới *</label>
                    <div className="relative">
                      <input
                        type={showNew ? "text" : "password"}
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        placeholder="Tối thiểu 6 ký tự"
                        className={inputCls}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={`${showNew ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Xác nhận mật khẩu mới *</label>
                    <div className="relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPwd}
                        onChange={(e) => setConfirmPwd(e.target.value)}
                        placeholder="Nhập lại mật khẩu mới"
                        className={`${inputCls} ${confirmPwd && confirmPwd !== newPwd ? "border-red-300 focus:border-red-400" : confirmPwd && confirmPwd === newPwd ? "border-green-300 focus:border-green-400" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={`${showConfirm ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                      </button>
                    </div>
                    {confirmPwd && confirmPwd !== newPwd && (
                      <p className="text-xs text-red-500 mt-1">Mật khẩu không khớp</p>
                    )}
                    {confirmPwd && confirmPwd === newPwd && (
                      <p className="text-xs text-green-600 mt-1">Mật khẩu khớp</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { setShowModal(false); setError(""); setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); }}
                      className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Hủy
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 py-2.5 text-sm font-semibold text-white bg-[#004A99] hover:bg-[#003a7a] rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      {saving && <i className="ri-loader-4-line animate-spin" />}
                      Cập nhật mật khẩu
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffChangePassword;
